import { useState, useEffect, useRef } from 'react';

interface KeyboardDetectionOptions {
  hideTabsOnKeyboard: boolean;
  enabled: boolean;
}

/**
 * Custom hook for detecting keyboard visibility
 * Fixes performance warning by using refs instead of direct DOM manipulation
 */
export const useKeyboardDetection = ({ 
  hideTabsOnKeyboard, 
  enabled 
}: KeyboardDetectionOptions) => {
  const [isKeyboardVisible, setIsKeyboardVisible] = useState(false);
  const focusHandlerRef = useRef<(e: Event) => void>();
  const blurHandlerRef = useRef<() => void>();

  useEffect(() => {
    if (!enabled || !hideTabsOnKeyboard) return;

    // Create handler functions using refs to avoid stale closures
    focusHandlerRef.current = (e: Event) => {
      const target = e.target as HTMLElement;
      if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.contentEditable === 'true') {
        setTimeout(() => setIsKeyboardVisible(true), 300);
      }
    };

    blurHandlerRef.current = () => {
      setTimeout(() => setIsKeyboardVisible(false), 300);
    };

    // Use event delegation instead of querying all inputs directly
    // This avoids direct DOM manipulation warning
    const handleFocusIn = (e: FocusEvent) => {
      if (focusHandlerRef.current) {
        focusHandlerRef.current(e);
      }
    };

    const handleFocusOut = () => {
      if (blurHandlerRef.current) {
        blurHandlerRef.current();
      }
    };

    // Attach listeners to document for event delegation
    document.addEventListener('focusin', handleFocusIn);
    document.addEventListener('focusout', handleFocusOut);

    // Visual viewport API for better keyboard detection
    let viewportCleanup: (() => void) | undefined;
    
    if ('visualViewport' in window && window.visualViewport) {
      const viewport = window.visualViewport;
      
      const handleViewportChange = () => {
        const isKeyboard = viewport.height < window.innerHeight * 0.75;
        setIsKeyboardVisible(isKeyboard);
      };

      viewport.addEventListener('resize', handleViewportChange);
      viewport.addEventListener('scroll', handleViewportChange);

      viewportCleanup = () => {
        viewport.removeEventListener('resize', handleViewportChange);
        viewport.removeEventListener('scroll', handleViewportChange);
      };
    }

    return () => {
      // Clean up event delegation listeners
      document.removeEventListener('focusin', handleFocusIn);
      document.removeEventListener('focusout', handleFocusOut);
      
      if (viewportCleanup) {
        viewportCleanup();
      }
    };
  }, [hideTabsOnKeyboard, enabled]);

  return { isKeyboardVisible };
};